import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { CircleDot, Gauge, Radio, Cpu, AlertTriangle, Car } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const DIRECT_STEPS = [
  { icon: CircleDot, title: "إطار السيارة", desc: "يعمل الإطار بضغط هواء محدد توصي به الشركة الصانعة لسيارتك." },
  { icon: Gauge, title: "حساس ضغط الإطار", desc: "حساس داخل الجنط يقيس ضغط الهواء فعليًا (وغالبًا الحرارة) بشكل مستمر." },
  { icon: Radio, title: "إرسال لاسلكي", desc: "يرسل الحساس البيانات لاسلكيًا بتردد مثل 433MHz أو 315MHz عندما يدعم النظام ذلك." },
  { icon: Cpu, title: "وحدة استقبال السيارة", desc: "تستقبل وحدة التحكم في السيارة البيانات وتطابقها مع الحدود المسموحة." },
  { icon: AlertTriangle, title: "قراءة أو تحذير", desc: "تظهر القراءة أو لمبة التحذير على لوحة العدادات عند انخفاض الضغط عن الحد." },
];

const INDIRECT_STEPS = [
  { icon: CircleDot, title: "إطار السيارة", desc: "عند انخفاض ضغط الإطار يقل قطره الفعلي قليلًا فيدور أسرع من بقية العجلات." },
  { icon: Car, title: "حساسات سرعة العجلات", desc: "يعتمد النظام على حساسات ABS الموجودة أصلًا لقياس سرعة دوران كل عجلة." },
  { icon: Cpu, title: "مقارنة إلكترونية", desc: "تقارن وحدة السيارة سرعات الدوران بين العجلات دون حساس ضغط مستقل داخل الإطار." },
  { icon: AlertTriangle, title: "تحذير على الطبلون", desc: "عند اكتشاف فرق في سرعة الدوران تضيء لمبة تحذير ضغط الإطارات." },
];

export const HowItWorks = () => {
  const [tab, setTab] = useState("direct");
  const [step, setStep] = useState(0);
  const reduce = useReducedMotion();
  const steps = tab === "direct" ? DIRECT_STEPS : INDIRECT_STEPS;
  const ActiveIcon = steps[step].icon;

  return (
    <section
      data-testid="tpms-explainer-section"
      className="bg-[#F8FAFC] py-20 sm:py-28 text-slate-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          light
          eyebrow="TPMS Technology"
          title="كيف يعمل نظام مراقبة ضغط الإطارات؟"
          desc="تصور مبسط لرحلة المعلومات من الإطار إلى لوحة العدادات، مع الفرق بين النظام المباشر وغير المباشر."
        />

        <Reveal className="flex justify-center mb-10">
          <div className="inline-flex bg-white border border-slate-200 rounded-full p-1.5 shadow-sm">
            {[
              { id: "direct", label: "النظام المباشر", testId: "tpms-tab-direct" },
              { id: "indirect", label: "النظام غير المباشر", testId: "tpms-tab-indirect" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={t.testId}
                onClick={() => {
                  setTab(t.id);
                  setStep(0);
                }}
                className={`relative px-5 sm:px-7 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                  tab === t.id ? "text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tab === t.id && (
                  <motion.span
                    layoutId="tpms-tab-pill"
                    className="absolute inset-0 bg-red-600 rounded-full"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
                <span className="relative z-10">{t.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <Reveal>
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${tab}-${step}`}
                  initial={reduce ? { opacity: 0 } : { opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 24 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mb-5">
                    <ActiveIcon size={26} className="text-red-600" />
                  </div>
                  <div className="font-mono text-xs text-slate-400 mb-1" dir="ltr">
                    STEP {step + 1} / {steps.length}
                  </div>
                  <h3 className="font-display text-xl font-bold mb-3">{steps[step].title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                    {steps[step].desc}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* شريط المراحل */}
              <div className="mt-8 flex items-center gap-1.5" dir="ltr">
                {steps.map((_, i) => (
                  <div key={i} className="flex-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div
                      className="h-full bg-red-600 rounded-full"
                      initial={false}
                      animate={{ width: i <= step ? "100%" : "0%" }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-slate-400 leading-relaxed border-t border-slate-100 pt-4">
                هذه الرسوم توضيحية لشرح مبدأ العمل فقط، ولا تمثل قياسًا حقيقيًا أو
                فحصًا مباشرًا لسيارتك.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="grid sm:grid-cols-2 gap-3">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <button
                  key={`${tab}-${i}`}
                  type="button"
                  data-testid={`tpms-step-${i + 1}`}
                  onClick={() => setStep(i)}
                  className={`text-right rounded-2xl border p-4 sm:p-5 transition-all ${
                    step === i
                      ? "bg-white border-red-300 shadow-[0_8px_30px_rgba(220,38,38,0.12)]"
                      : "bg-white/70 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        step === i ? "bg-red-600 text-white" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <Icon size={16} />
                    </span>
                    <span className="font-mono text-[10px] text-slate-400" dir="ltr">
                      0{i + 1}
                    </span>
                  </div>
                  <div className="font-semibold text-sm text-slate-900">{s.title}</div>
                </button>
              );
            })}
            <div className="sm:col-span-2 rounded-2xl bg-slate-900 text-slate-200 p-5 text-sm leading-relaxed">
              {tab === "direct"
                ? "النظام المباشر يستخدم حساس ضغط مستقلًا داخل كل إطار، لذا تكون القراءة أدق وأسرع استجابة."
                : "النظام غير المباشر لا يحتوي على حساس ضغط داخل الإطار، بل يستنتج انخفاض الضغط من فرق سرعة دوران العجلات، لذا قد يتأخر التحذير ويحتاج إلى إعادة معايرة بعد ضبط الهواء."}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
